import { BehaviorActionType } from '@/common/model/recommend/behavior'

export const AnalyticsApiPath = {
  GET_OVERVIEW: '/api/analytics/overview', // GET Get Overview
  GET_AB_RESULTS: '/api/analytics/ab-results', // GET Get Ab Results
  GET_ALGORITHM_PERFORMANCE: '/api/analytics/algorithm-performance', // GET Get Algorithm Performance
  GET_RECOMMENDATION_PERFORMANCE: '/api/analytics/recommendation-performance', // GET Get Recommendation Performance
  GET_SERVICE_SUMMARY: '/api/analytics/service-summary', // GET Get Service Summary
  GET_ALERTS: '/api/analytics/alerts' // GET Get Alerts
}

export type AnalyticsApiPath = (typeof AnalyticsApiPath)[keyof typeof AnalyticsApiPath]

export interface GetOverviewVars {}

export interface GetAbResultsVars {}

export interface GetAlgorithmPerformanceVars {}

export interface GetRecommendationPerformanceVars {
  site_id?: number
  month?: string
}

export interface GetOverviewResponse {
  total_sites: number
  paid_sites: number
  active_sites_30d: number
  total_users: number
  total_products: number
  total_events: number
  total_recommendations: number
  events_by_type: Record<BehaviorActionType, number>
}

export const ServiceSummaryKey = {
  DATA_INTEGRATION: 'data_integration',
  RECO_MEDIA: 'reco_media',
  ARTICLE_UPLOAD: 'article_upload',
  GEO_LLMO: 'geo_llmo',
  REVIEW: 'review',
  AI_PURCHASE_ASSIST: 'ai_purchase_assist',
  CARTE: 'carte',
  PERSONA: 'persona',
  // 2026-10-06 追加: メールレコメンド（Reco-Media のオプション）
  MAIL_RECO: 'mail_reco'
} as const

export type ServiceSummaryKey = (typeof ServiceSummaryKey)[keyof typeof ServiceSummaryKey]

export interface ServiceEffectDataIntegration {
  error_rate: number
  failed_count: number
  total_count: number
}
export interface ServiceEffectRecoMedia {
  ctr: number
  cvr: number
  revenue: number
  impressions: number
  clicks: number
  conversions: number
}
export interface ServiceEffectArticleUpload {
  published_count: number
  request_count: number
  publish_rate: number
}
export interface ServiceEffectGeoLlmo {
  avg_seo_score: number | null
}
export interface ServiceEffectReview {
  submitted_count: number
  avg_rating: number | null
  ugc_origin_purchase: number
}
export interface ServiceEffectAiPurchaseAssist {
  cvr: number
  sessions: number
  purchase_24h_count: number
  via_click_count: number
  revenue: number
}

export interface ServiceEffectPersona {
  adopted_personas: number
  judged_customers: number
  /** 名寄せ対象の訪問者が0のときは率が出せないため null（0% とは区別する） */
  identity_rate: number | null
}

export type ServiceEffect =
  | ServiceEffectDataIntegration
  | ServiceEffectRecoMedia
  | ServiceEffectArticleUpload
  | ServiceEffectGeoLlmo
  | ServiceEffectReview
  | ServiceEffectAiPurchaseAssist
  | ServiceEffectPersona
  | null

export interface ServiceSummaryItem {
  key: ServiceSummaryKey
  name: string
  enabled_sites: number
  total_sites: number
  adoption_rate: number
  active_sites_30d: number
  count_30d: number
  count_prev_30d: number
  trend_pct: number | null
  last_update_at: string | null
  daily_counts: { date: string; count: number }[]
  effect: ServiceEffect
}

export interface GetServiceSummaryVars {}

export interface GetServiceSummaryResponse {
  services: ServiceSummaryItem[]
  as_of: string
}

export type AlertSeverity = 'critical' | 'warning' | 'info'

export interface DashboardAlert {
  service: ServiceSummaryKey
  severity: AlertSeverity
  site_id: number
  site_name: string
  title: string
  detail: string
}

export interface GetAlertsVars {}

export interface GetAlertsResponse {
  alerts: DashboardAlert[]
  counts: Record<AlertSeverity, number>
  as_of: string
}

export type GetAbResultsResponse = Record<
  string,
  { total_recommendations: number; clicked: number; ctr: number }
>

export const AlgorithmType = {
  CONTENT_BASED: 'content_based',
  HYBRID: 'hybrid',
  COLLABORATIVE: 'collaborative',
  POPULARITY: 'popularity'
} as const

export type AlgorithmType = (typeof AlgorithmType)[keyof typeof AlgorithmType]

export type GetAlgorithmPerformanceResponse = Record<
  AlgorithmType,
  { total_recommendations: number; clicked: number; ctr: number }
>

export interface GetRecommendationPerformanceResponse {
  month: string
  month_label: string
  total_impressions: number
  total_clicks: number
  // 従来キー（total_conversions / total_revenue と daily の conversions / revenue）は直接CVを指す
  total_conversions: number
  total_revenue: number
  total_direct_conversions: number
  total_direct_revenue: number
  total_assist_conversions: number
  total_assist_revenue: number
  ctr: number
  cvr_conversion: number
  daily: {
    date: string
    impressions: number
    clicks: number
    conversions: number
    revenue: number
    direct_conversions: number
    direct_revenue: number
    assist_conversions: number
    assist_revenue: number
  }[]
}
